import { useState, useEffect } from 'react';

// Гиппер-сводная (read-only): BU→ТМ→СВ→Точка(ship_to). Недели колонками: Планир./Заказ + %.
// Клик по точке раскрывает планирование по дням. Данные — из hdata.daily (уже загружены).
// roots: [{ key, label, sub?, pointIds:[id...], children?, pointKey? }] (лист = точка с pointKey)
const DOW_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const SL_TARGET = 95;

export default function HyperPivot({ roots, weekNums, weeks, daily, focusKey }) {
  const [open, setOpen] = useState({});
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }));

  useEffect(() => {
    if (!focusKey) return;
    const path = {};
    const walk = (node, chain) => {
      if (node.pointKey === focusKey) { chain.concat(node.key).forEach(kk => { path[kk] = true; }); return true; }
      if (node.children) { for (const c of node.children) if (walk(c, [...chain, node.key])) { chain.concat(node.key).forEach(kk => { path[kk] = true; }); return true; } }
      return false;
    };
    roots.forEach(r => walk(r, []));
    setOpen(o => ({ ...o, ...path }));
    setTimeout(() => document.getElementById(`hp-${focusKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
  }, [focusKey, roots]);

  const datesOf = w => (weeks[w]?.days || []).map(d => ({ ...d }));
  const sumFor = (pointIds) => {
    const per = {}; let tPlan = 0, tOrd = 0;
    weekNums.forEach(w => {
      let plan = 0, ord = 0;
      const dates = datesOf(w).map(d => d.date);
      pointIds.forEach(pid => dates.forEach(dt => { const c = daily[pid]?.[dt]; if (!c) return; ord += c.order || 0; if (c.plan != null) plan += c.plan; }));
      per[w] = { plan, ord };
      tPlan += plan; tOrd += ord;
    });
    return { per, tPlan, tOrd, pct: tOrd ? Math.round(tPlan / tOrd * 100) : null };
  };

  const colW = 52, itogW = 66, labelW = 190;
  const cellColor = (plan, ord) => {
    if (!ord) return { c: 'var(--text-light)', b: 'transparent' };
    const p = Math.round(plan / ord * 100);
    return p >= SL_TARGET ? { c: '#137a54', b: '#E4F2EA' } : { c: '#b23', b: '#FDECE8' };
  };

  const renderNode = (node, depth) => {
    const s = sumFor(node.pointIds);
    const isLeaf = !node.children;
    const k = node.key;
    const expandable = (node.children && node.children.length) || isLeaf;
    const rows = [];
    rows.push(
      <div key={k} id={isLeaf ? `hp-${node.pointKey}` : undefined}
        onClick={() => toggle(k)}
        style={{ display: 'flex', alignItems: 'flex-start', borderBottom: '1px solid var(--border)', cursor: expandable ? 'pointer' : 'default',
          background: depth === 0 ? '#EEF3FA' : node.pointKey === focusKey ? '#FFF7DA' : '#fff' }}>
        <div style={{ width: labelW, minWidth: labelW, padding: '6px 8px', paddingLeft: 8 + depth * 14, fontSize: 12, fontWeight: depth <= 1 ? 700 : 500, display: 'flex', alignItems: 'flex-start', gap: 6, position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
          {expandable && <span style={{ fontSize: 9, color: 'var(--text-muted)', width: 8, marginTop: 2 }}>{open[k] ? '▼' : '▶'}</span>}
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.label}</span>
            {node.sub && <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: 'var(--text-light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.sub}</span>}
            {node.addr && <span title={node.addr} style={{ display: 'block', fontSize: 9, fontWeight: 400, color: 'var(--text-light)', whiteSpace: 'normal', lineHeight: 1.15, marginTop: 1 }}>📍 {node.addr}</span>}
          </span>
        </div>
        {weekNums.map(w => {
          const { plan, ord } = s.per[w]; const col = cellColor(plan, ord);
          const pct = ord ? Math.round(plan / ord * 100) : null;
          return <div key={w} style={{ width: colW, minWidth: colW, textAlign: 'center', fontSize: 11, padding: '5px 2px', background: col.b, color: col.c, fontVariantNumeric: 'tabular-nums' }}>
            <div>{plan}<span style={{ color: 'var(--text-light)' }}>/{ord}</span></div>
            <div style={{ fontSize: 9, fontWeight: 700 }}>{pct == null ? '—' : pct + '%'}</div>
          </div>;
        })}
        <div style={{ width: itogW, minWidth: itogW, textAlign: 'center', fontSize: 11, padding: '5px 2px', fontWeight: 700, background: '#F4F7FB' }}>
          {s.tPlan}<span style={{ color: 'var(--text-light)', fontWeight: 400 }}>/{s.tOrd}</span>
          <div style={{ fontSize: 10, color: s.pct == null ? 'var(--text-light)' : s.pct >= SL_TARGET ? '#137a54' : '#b23' }}>{s.pct == null ? '—' : s.pct + '%'}</div>
        </div>
      </div>
    );
    if (!isLeaf && open[k]) node.children.forEach(c => rows.push(...renderNode(c, depth + 1)));
    if (isLeaf && open[k]) rows.push(renderPointDays(node, depth));
    return rows;
  };

  function renderPointDays(node, depth) {
    return (
      <div key={`d-${node.pointKey}`} style={{ padding: '6px 8px 10px', paddingLeft: 8 + (depth + 1) * 14, background: '#FBFCFE', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {weekNums.map(w => {
          const wd = (weeks[w]?.days || []);
          if (!wd.length) return null;
          return (
            <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
              <div style={{ width: 40, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>W{w}</div>
              {wd.map(({ day, dow, date }) => {
                let ord = 0, plan = null, fact = null;
                node.pointIds.forEach(pid => { const c = daily[pid]?.[date]; if (!c) return; ord += c.order || 0; if (c.plan != null) plan = (plan || 0) + c.plan; if (c.fact != null) fact = (fact || 0) + c.fact; });
                const col = cellColor(plan || 0, ord);
                return <div key={date} style={{ width: 40, textAlign: 'center', fontSize: 10 }}>
                  <div style={{ color: 'var(--text-light)', fontSize: 8 }}>{DOW_RU[dow]}<br/>{day}</div>
                  <div style={{ borderRadius: 4, padding: '2px 1px', background: col.b, color: col.c, fontWeight: 700, lineHeight: 1.1 }}>
                    <div>{plan == null ? '·' : plan}</div>
                    <div style={{ fontSize: 8, color: 'var(--text-light)' }}>{fact == null ? '' : `ф${fact}`}</div>
                  </div>
                </div>;
              })}
            </div>
          );
        })}
        <div style={{ fontSize: 9, color: 'var(--text-light)', marginTop: 2 }}>Число — планирование по дню; «ф» — факт BI; редактируется на роли СВ.</div>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: labelW + colW * weekNums.length + itogW }}>
          {/* Общее выполнение по всем точкам: над номерами недель */}
          {(() => {
            const t = sumFor([...new Set(roots.flatMap(r => r.pointIds || []))]);
            return (
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: '#F4F7FB' }}>
                <div style={{ width: labelW, minWidth: labelW, padding: '6px 8px', fontSize: 11, fontWeight: 700, color: '#1F3864', position: 'sticky', left: 0, background: '#F4F7FB', zIndex: 2 }}>Общее выполнение</div>
                {weekNums.map(w => {
                  const { plan, ord } = t.per[w]; const col = cellColor(plan, ord);
                  const pct = ord ? Math.round(plan / ord * 100) : null;
                  return <div key={w} style={{ width: colW, minWidth: colW, textAlign: 'center', fontSize: 11, padding: '4px 2px', fontWeight: 700, color: col.c, fontVariantNumeric: 'tabular-nums' }}>
                    <div style={{ fontSize: 13 }}>{pct == null ? '—' : pct + '%'}</div>
                    <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-light)' }}>{plan}/{ord}</div>
                  </div>;
                })}
                <div style={{ width: itogW, minWidth: itogW, textAlign: 'center', fontSize: 11, padding: '4px 2px', fontWeight: 700, color: t.pct == null ? 'var(--text-light)' : t.pct >= SL_TARGET ? '#137a54' : '#b23' }}>
                  <div style={{ fontSize: 13 }}>{t.pct == null ? '—' : t.pct + '%'}</div>
                  <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-light)' }}>{t.tPlan}/{t.tOrd}</div>
                </div>
              </div>
            );
          })()}
          <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', background: '#1F3864', color: '#fff' }}>
            <div style={{ width: labelW, minWidth: labelW, padding: '8px', fontSize: 11, fontWeight: 700, position: 'sticky', left: 0, background: '#1F3864', zIndex: 2 }}>Планир. / Заказ</div>
            {weekNums.map(w => <div key={w} style={{ width: colW, minWidth: colW, textAlign: 'center', padding: '8px 2px', fontSize: 11, fontWeight: 700 }}>W{w}</div>)}
            <div style={{ width: itogW, minWidth: itogW, textAlign: 'center', padding: '8px 2px', fontSize: 11, fontWeight: 700 }}>Итог · %</div>
          </div>
          {roots.map(r => renderNode(r, 0))}
        </div>
      </div>
    </div>
  );
}
